import { useState } from 'react';
import type { AdRewardConfig, AdRewardCopyConfig } from '../lib/adReward';
import { defaultAdRewardCopy } from '../lib/adReward';

type Props = {
  cfg: AdRewardConfig;
  onChange: (patch: Partial<AdRewardConfig>) => void;
};

function applyPinGroup(patch: Partial<AdRewardConfig>, current: AdRewardConfig): AdRewardConfig {
  const next = { ...current, ...patch, mode: 'pin' as const };
  next.refresh_owner_enabled = false;
  next.refresh_other_enabled = false;
  return next;
}

function applyRefreshGroup(patch: Partial<AdRewardConfig>, current: AdRewardConfig): AdRewardConfig {
  const next = { ...current, ...patch, mode: 'refresh' as const };
  next.pin_owner_enabled = false;
  next.pin_other_enabled = false;
  return next;
}

function patchCopy(cfg: AdRewardConfig, copyPatch: Partial<AdRewardCopyConfig>): AdRewardConfig {
  return { ...cfg, copy: { ...cfg.copy, ...copyPatch } };
}

function CopyField({
  label,
  hint,
  value,
  disabled,
  multiline,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  disabled?: boolean;
  multiline?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block text-sm text-slate-700">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {hint ? <span className="mb-1 block text-[11px] text-slate-400">{hint}</span> : null}
      {multiline ? (
        <textarea
          rows={3}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type="text"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

export function MiniprogramAdRewardSettingsCard({ cfg, onChange }: Props) {
  const pinActive = cfg.mode === 'pin';
  const refreshActive = cfg.mode === 'refresh';
  const [copyOpen, setCopyOpen] = useState(false);
  const copy = cfg.copy ?? defaultAdRewardCopy();

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-slate-800">A 组：激励视频送平台置顶</h4>
        <p className="mt-1 text-xs text-slate-500">与 B 组互斥。开启任一项后，B 组自动关闭。</p>
        <div className={`mt-3 space-y-3 ${refreshActive ? 'pointer-events-none opacity-45' : ''}`}>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-white p-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={!!cfg.pin_owner_enabled}
              disabled={refreshActive}
              onChange={(e) => {
                const on = e.target.checked;
                onChange(
                  applyPinGroup(
                    {
                      pin_owner_enabled: on,
                      pin_other_enabled: on ? cfg.pin_other_enabled : cfg.pin_other_enabled,
                    },
                    cfg,
                  ),
                );
              }}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-800">开启：发布人自己看激励广告送平台置顶</span>
              <span className="mt-1 block text-xs text-slate-500">发布成功后引导置顶；详情页与「我的发布」可继续叠加天数。</span>
            </span>
          </label>
          {cfg.pin_owner_enabled ? (
            <div className="space-y-3 pl-7">
              <label className="block text-sm text-slate-700">
                <span className="mb-1 block text-xs font-medium text-slate-600">赠送置顶天数（每次完整观看，可叠加）</span>
                <input
                  type="number"
                  min={1}
                  max={30}
                  className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={cfg.pin_reward_days ?? 3}
                  disabled={refreshActive}
                  onChange={(e) =>
                    onChange(
                      applyPinGroup(
                        { pin_reward_days: Math.min(30, Math.max(1, Number(e.target.value) || 3)) },
                        cfg,
                      ),
                    )
                  }
                />
              </label>
              <label className="block text-sm text-slate-700">
                <span className="mb-1 block text-xs font-medium text-slate-600">发布人每日自己置顶次数（0 表示不限）</span>
                <input
                  type="number"
                  min={0}
                  max={99}
                  className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={cfg.pin_owner_daily_limit ?? 3}
                  disabled={refreshActive}
                  onChange={(e) =>
                    onChange(
                      applyPinGroup(
                        {
                          pin_owner_daily_limit: Math.min(99, Math.max(0, Number(e.target.value) || 0)),
                        },
                        cfg,
                      ),
                    )
                  }
                />
              </label>
            </div>
          ) : null}
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-white p-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={!!cfg.pin_other_enabled}
              disabled={refreshActive}
              onChange={(e) =>
                onChange(
                  applyPinGroup({ pin_other_enabled: e.target.checked }, cfg),
                )
              }
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-800">开启：非发布人看激励广告送平台置顶</span>
              <span className="mt-1 block text-xs text-slate-500">已登录用户可为他人信息助力置顶（如亲友代发场景）。</span>
            </span>
          </label>
          {cfg.pin_other_enabled ? (
            <label className="block text-sm text-slate-700 pl-7">
              <span className="mb-1 block text-xs font-medium text-slate-600">每人每日帮他人置顶次数（0 表示不限）</span>
              <input
                type="number"
                min={0}
                max={99}
                className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={cfg.pin_other_daily_limit ?? 5}
                disabled={refreshActive}
                onChange={(e) =>
                  onChange(
                    applyPinGroup(
                      {
                        pin_other_daily_limit: Math.min(99, Math.max(0, Number(e.target.value) || 0)),
                      },
                      cfg,
                    ),
                  )
                }
              />
            </label>
          ) : null}
        </div>
      </div>

      <div className="border-t border-slate-200/80 pt-5">
        <h4 className="text-sm font-semibold text-slate-800">B 组：激励视频送信息刷新</h4>
        <p className="mt-1 text-xs text-slate-500">刷新后按列表「刷新时间」排序靠前；与 A 组互斥。</p>
        <div className={`mt-3 space-y-3 ${pinActive ? 'pointer-events-none opacity-45' : ''}`}>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-white p-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={!!cfg.refresh_owner_enabled}
              disabled={pinActive}
              onChange={(e) =>
                onChange(
                  applyRefreshGroup({ refresh_owner_enabled: e.target.checked }, cfg),
                )
              }
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-800">开启：发布人自己看激励广告送信息刷新</span>
              <span className="mt-1 block text-xs text-slate-500">发布超过设定时长后可自动引导刷新；每次刷新不叠加，仅更新排序时间。</span>
            </span>
          </label>
          {cfg.refresh_owner_enabled ? (
            <div className="space-y-3 pl-7">
              <label className="block text-sm text-slate-700">
                <span className="mb-1 block text-xs font-medium text-slate-600">自动引导刷新（发布后满小时数）</span>
                <input
                  type="number"
                  min={1}
                  max={168}
                  className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={cfg.refresh_owner_auto_hours ?? 24}
                  disabled={pinActive}
                  onChange={(e) =>
                    onChange(
                      applyRefreshGroup(
                        {
                          refresh_owner_auto_hours: Math.min(
                            168,
                            Math.max(1, Number(e.target.value) || 24),
                          ),
                        },
                        cfg,
                      ),
                    )
                  }
                />
              </label>
              <label className="block text-sm text-slate-700">
                <span className="mb-1 block text-xs font-medium text-slate-600">发布人每日自己刷新次数（0 表示不限）</span>
                <input
                  type="number"
                  min={0}
                  max={99}
                  className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={cfg.refresh_owner_daily_limit ?? 3}
                  disabled={pinActive}
                  onChange={(e) =>
                    onChange(
                      applyRefreshGroup(
                        {
                          refresh_owner_daily_limit: Math.min(99, Math.max(0, Number(e.target.value) || 0)),
                        },
                        cfg,
                      ),
                    )
                  }
                />
              </label>
            </div>
          ) : null}
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-white p-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={!!cfg.refresh_other_enabled}
              disabled={pinActive}
              onChange={(e) =>
                onChange(
                  applyRefreshGroup({ refresh_other_enabled: e.target.checked }, cfg),
                )
              }
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-800">开启：非发布人看激励广告送信息刷新</span>
              <span className="mt-1 block text-xs text-slate-500">已登录用户可为他人信息助力刷新排序。</span>
            </span>
          </label>
          {cfg.refresh_other_enabled ? (
            <label className="block text-sm text-slate-700 pl-7">
              <span className="mb-1 block text-xs font-medium text-slate-600">每人每日帮他人刷新次数（0 表示不限）</span>
              <input
                type="number"
                min={0}
                max={99}
                className="w-full max-w-[160px] rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={cfg.refresh_other_daily_limit ?? 5}
                disabled={pinActive}
                onChange={(e) =>
                  onChange(
                    applyRefreshGroup(
                      {
                        refresh_other_daily_limit: Math.min(99, Math.max(0, Number(e.target.value) || 0)),
                      },
                      cfg,
                    ),
                  )
                }
              />
            </label>
          ) : null}
        </div>
      </div>

      {(pinActive || refreshActive) && (
        <>
          <div className="border-t border-slate-200/80 pt-5">
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800"
              onClick={() => setCopyOpen((v) => !v)}
            >
              <span>弹窗文案配置（可自定义，留空则用默认）</span>
              <span className="text-slate-400">{copyOpen ? '收起' : '展开'}</span>
            </button>
            {copyOpen ? (
              <div className="mt-3 space-y-4 rounded-lg border border-slate-100 bg-white p-3">
                <p className="text-[11px] text-slate-400">
                  变量：{'{days}'} 置顶天数、{'{hours}'} 自动引导小时数、{'{remaining}'} 今日剩余、{'{limit}'} 每日上限
                </p>
                {pinActive ? (
                  <div className="space-y-3">
                    <h5 className="text-xs font-semibold text-amber-800">置顶 · 发布人</h5>
                    <CopyField label="引导弹窗标题" value={copy.pin_owner_title} onChange={(v) => onChange(patchCopy(cfg, { pin_owner_title: v }))} />
                    <CopyField label="引导弹窗内容" hint="支持 {days}" multiline value={copy.pin_owner_body} onChange={(v) => onChange(patchCopy(cfg, { pin_owner_body: v }))} />
                    <CopyField label="成功提示" hint="支持 {days}" value={copy.pin_owner_success} onChange={(v) => onChange(patchCopy(cfg, { pin_owner_success: v }))} />
                    <CopyField label="次数用尽 · 按钮文案" value={copy.pin_owner_exhausted_btn} onChange={(v) => onChange(patchCopy(cfg, { pin_owner_exhausted_btn: v }))} />
                    <CopyField label="次数用尽 · 弹窗标题" value={copy.pin_owner_exhausted_title} onChange={(v) => onChange(patchCopy(cfg, { pin_owner_exhausted_title: v }))} />
                    <CopyField label="次数用尽 · 弹窗内容" multiline value={copy.pin_owner_exhausted_body} onChange={(v) => onChange(patchCopy(cfg, { pin_owner_exhausted_body: v }))} />
                    <h5 className="pt-2 text-xs font-semibold text-amber-800">置顶 · 非发布人</h5>
                    <CopyField label="引导弹窗标题" value={copy.pin_other_title} onChange={(v) => onChange(patchCopy(cfg, { pin_other_title: v }))} />
                    <CopyField label="引导弹窗内容" hint="支持 {days}" multiline value={copy.pin_other_body} onChange={(v) => onChange(patchCopy(cfg, { pin_other_body: v }))} />
                    <CopyField label="成功提示" hint="支持 {days}" value={copy.pin_other_success} onChange={(v) => onChange(patchCopy(cfg, { pin_other_success: v }))} />
                  </div>
                ) : null}
                {refreshActive ? (
                  <div className="space-y-3">
                    <h5 className="text-xs font-semibold text-teal-800">刷新 · 发布人</h5>
                    <CopyField label="引导弹窗标题" value={copy.refresh_owner_title} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_title: v }))} />
                    <CopyField label="引导弹窗内容" multiline value={copy.refresh_owner_body} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_body: v }))} />
                    <CopyField label="自动引导弹窗内容" hint="支持 {hours}" multiline value={copy.refresh_owner_auto_body} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_auto_body: v }))} />
                    <CopyField label="成功提示" value={copy.refresh_owner_success} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_success: v }))} />
                    <CopyField label="次数用尽 · 按钮文案" value={copy.refresh_owner_exhausted_btn} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_exhausted_btn: v }))} />
                    <CopyField label="次数用尽 · 弹窗标题" value={copy.refresh_owner_exhausted_title} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_exhausted_title: v }))} />
                    <CopyField label="次数用尽 · 弹窗内容" multiline value={copy.refresh_owner_exhausted_body} onChange={(v) => onChange(patchCopy(cfg, { refresh_owner_exhausted_body: v }))} />
                    <h5 className="pt-2 text-xs font-semibold text-teal-800">刷新 · 非发布人</h5>
                    <CopyField label="引导弹窗标题" value={copy.refresh_other_title} onChange={(v) => onChange(patchCopy(cfg, { refresh_other_title: v }))} />
                    <CopyField label="引导弹窗内容" multiline value={copy.refresh_other_body} onChange={(v) => onChange(patchCopy(cfg, { refresh_other_body: v }))} />
                    <CopyField label="成功提示" value={copy.refresh_other_success} onChange={(v) => onChange(patchCopy(cfg, { refresh_other_success: v }))} />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <p className="rounded-lg border border-amber-200/90 bg-amber-50/60 px-3 py-2 text-xs text-amber-950">
            请确认已在「运营扩展 → 流量主广告」中开启激励视频并填写广告位 ID。
          </p>
        </>
      )}
    </div>
  );
}
